import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { convertImage } from '../../../../../services/contents';
import { truncateText } from '../../../../../services/utils';
import Colors from '../../../../../themes/Colors';

const ResolutionConflictModal = ({
  visible,
  conflictGroups,
  existingByResolution = {},
  paymentStatus,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();

  const resolutionKeys = Object.keys(conflictGroups || {});
  const hasExistingConflicts = Object.keys(existingByResolution || {}).length > 0;

  const subtitle = (() => {
    if (hasExistingConflicts && paymentStatus === 'approved') {
      return t(
        'Selected ads will deactivate the current versions, preserving already delivered spots.'
      );
    }
    if (hasExistingConflicts) {
      return t(
        'Selected ads will replace the current versions. Additional selections become new versions.'
      );
    }
    return t(
      'If you select more than one ad per resolution, new versions will be created automatically.'
    );
  })();

  const [selectedByResolution, setSelectedByResolution] = useState({});

  useEffect(() => {
    if (!visible) return;
    const emptySelection = {};
    resolutionKeys.forEach(key => {
      emptySelection[key] = [];
    });
    setSelectedByResolution(emptySelection);
  }, [visible]);

  const totalItems = resolutionKeys.reduce(
    (sum, key) => sum + (conflictGroups[key] || []).length,
    0
  );

  const totalSelected = resolutionKeys.reduce(
    (sum, key) => sum + (selectedByResolution[key] || []).length,
    0
  );

  const isGlobalChecked = totalItems > 0 && totalSelected === totalItems;

  const canConfirm =
    resolutionKeys.length > 0 &&
    resolutionKeys.every(key => (selectedByResolution[key] || []).length > 0);

  const handleToggleGlobal = checked => {
    const newSelection = {};
    resolutionKeys.forEach(key => {
      newSelection[key] = checked ? (conflictGroups[key] || []).map(c => c.id) : [];
    });
    setSelectedByResolution(newSelection);
  };

  const handleToggleGroup = (resolutionKey, checked) => {
    setSelectedByResolution(prev => ({
      ...prev,
      [resolutionKey]: checked ? (conflictGroups[resolutionKey] || []).map(c => c.id) : []
    }));
  };

  const handleToggleItem = (resolutionKey, contentId, checked) => {
    setSelectedByResolution(prev => {
      const currentIds = prev[resolutionKey] || [];
      const updatedIds = checked
        ? [...currentIds, contentId]
        : currentIds.filter(id => id !== contentId);
      return { ...prev, [resolutionKey]: updatedIds };
    });
  };

  const handleConfirm = () => {
    const result = {};
    resolutionKeys.forEach(key => {
      const selectedIds = selectedByResolution[key] || [];
      result[key] = (conflictGroups[key] || []).filter(c => selectedIds.includes(c.id));
    });
    onConfirm(result);
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      title={t('Review of duplicate ads')}
      width={760}
      bodyStyle={{ maxHeight: '65vh', overflowY: 'auto', padding: '16px 24px' }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('Cancel')}
        </Button>,
        <Tooltip
          key="confirm"
          title={!canConfirm ? t('You must select at least one ad') : ''}
        >
          <span>
            <Button
              type="primary"
              disabled={!canConfirm}
              onClick={handleConfirm}
              style={{ marginLeft: 8 }}
            >
              {t('Add selected')}
            </Button>
          </span>
        </Tooltip>
      ]}
    >
      <p style={{ marginBottom: 16, color: Colors.textSecondary }}>{subtitle}</p>

      <div style={{ marginBottom: 12 }}>
        <Checkbox
          checked={isGlobalChecked}
          onChange={e => handleToggleGlobal(e.target.checked)}
        >
          {t('Select all ads')}
        </Checkbox>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, borderTop: `1px solid ${Colors.border}`, paddingTop: 12 }}>
        {resolutionKeys.map(resolutionKey => {
          const groupContents = conflictGroups[resolutionKey] || [];
          const selectedIds = selectedByResolution[resolutionKey] || [];
          const isGroupChecked = groupContents.length > 0 && selectedIds.length === groupContents.length;

          return (
            <div
              key={resolutionKey}
              style={{
                background: Colors.surfaceMuted,
                borderRadius: 8,
                padding: '12px 16px 14px 16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <Checkbox
                  checked={isGroupChecked}
                  onChange={e => handleToggleGroup(resolutionKey, e.target.checked)}
                >
                  <span style={{ fontWeight: 600 }}>
                    {resolutionKey.replace('x', ' × ')}
                  </span>
                </Checkbox>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {groupContents.map(content => {
                  const isItemSelected = selectedIds.includes(content.id);
                  return (
                    <div
                      key={content.id}
                      onClick={() => handleToggleItem(resolutionKey, content.id, !isItemSelected)}
                      style={{
                        border: `2px solid ${isItemSelected ? Colors.primaryAccent : '${Colors.borderStrong}'}`,
                        borderRadius: 8,
                        padding: '10px 10px 8px 10px',
                        cursor: 'pointer',
                        flex: '1 1 140px',
                        maxWidth: 200,
                        background: isItemSelected ? Colors.extraLightPrimary : '#fff',
                        transition: 'border-color 0.2s, background 0.2s',
                        userSelect: 'none'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <Checkbox
                          checked={isItemSelected}
                          onChange={e => {
                            e.stopPropagation();
                            handleToggleItem(resolutionKey, content.id, e.target.checked);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                        <div style={{ marginLeft: 6, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <Tooltip title={content.name}>
                            <span
                              style={{
                                fontSize: 12,
                                display: 'inline-block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                verticalAlign: 'middle'
                              }}
                            >
                              {truncateText(content.name, 16)}
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      <img
                        src={convertImage(content.file_thumb)}
                        alt=""
                        style={{
                          width: '100%',
                          height: 110,
                          objectFit: 'scale-down',
                          borderRadius: 4,
                          display: 'block'
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ResolutionConflictModal;
